import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MessageSquare, Send, Search, Plus, Users, User, ArrowLeft, Hash, Megaphone, Loader2, Trash2, MoreVertical } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const ROLE_COLORS = {
  super_admin: 'text-red-500', admin: 'text-orange-500', team_leader: 'text-purple-500',
  sales_executive: 'text-blue-500', cs_head: 'text-emerald-500', cs_agent: 'text-teal-500',
  mentor: 'text-yellow-600', hr: 'text-rose-500', finance: 'text-cyan-500',
};

const STATUS_DOT = { online: 'bg-emerald-400', away: 'bg-amber-400', offline: 'bg-slate-400' };

const formatRole = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' });
};

export default function ChatPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetConversationId = searchParams.get('conversation');
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await apiClient.get('/chat/conversations');
      setConversations(res.data || []);
    } catch (e) { logger.error('Chat fetch error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-open the conversation requested via ?conversation= URL param
  // (used by push notification click → navigate). Clear the param once
  // we've matched so refreshing the page doesn't keep re-opening.
  useEffect(() => {
    if (!targetConversationId || conversations.length === 0) return;
    if (activeConvo?.id === targetConversationId) return;
    const match = conversations.find(c => c.id === targetConversationId);
    if (match) {
      setActiveConvo(match);
      setShowMobile(true);
      setSearchParams({}, { replace: true });
    }
  }, [targetConversationId, conversations, activeConvo?.id, setSearchParams]);

  // Poll for new messages + heartbeat
  useEffect(() => {
    // Send initial heartbeat
    apiClient.post('/chat/heartbeat').catch(() => {});
    
    pollRef.current = setInterval(() => {
      fetchConversations();
      apiClient.post('/chat/heartbeat').catch(() => {});
      if (activeConvo) fetchMessages(activeConvo.id, true);
    }, 5000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConvo, fetchConversations]);

  const fetchMessages = async (convoId, silent = false) => {
    if (!silent) setMsgLoading(true);
    try {
      const res = await apiClient.get(`/chat/conversations/${convoId}/messages?limit=100`);
      setMessages(res.data.messages || []);
      setTimeout(scrollToBottom, 100);
    } catch (e) { logger.error(e); }
    finally { if (!silent) setMsgLoading(false); }
  };

  const openConversation = (convo) => {
    setActiveConvo(convo);
    setShowMobile(true);
    fetchMessages(convo.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvo || sending) return;
    setSending(true);
    try {
      const res = await apiClient.post(`/chat/conversations/${activeConvo.id}/messages`, { text: newMessage.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setTimeout(scrollToBottom, 50);
      fetchConversations();
    } catch (e) { logger.error(e); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = async (targetUser) => {
    try {
      const res = await apiClient.post('/chat/conversations', { participant_ids: [targetUser.id] });
      setShowNewChat(false);
      setUserSearch('');
      await fetchConversations();
      openConversation(res.data);
    } catch (e) { logger.error(e); }
  };

  // Group chat
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);

  const canCreateGroup = ['super_admin', 'admin', 'sales_manager', 'team_leader', 'cs_head',
    'master_of_academics', 'master_of_academics_', 'business_development_manager_', 'business_development_manager'].includes(user?.role);
  const canBroadcast = ['super_admin', 'admin', 'ceo'].includes(user?.role);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);

  const sendBroadcast = async () => {
    const text = (broadcastText || '').trim();
    if (!text) return;
    setBroadcastSending(true);
    try {
      const { data } = await apiClient.post('/chat/broadcast', { text });
      toast.success(`Announcement sent to ${data.recipients} people`);
      setBroadcastText('');
      setBroadcastOpen(false);
      // Refresh conversations + jump into the announcements thread
      await fetchConversations();
      setSearchParams({ conversation: data.conversation_id }, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setBroadcastSending(false);
    }
  };

  const openCreateGroupDialog = async () => {
    setShowCreateGroup(true);
    setGroupName('');
    setSelectedMembers([]);
    setUserSearch('');
    try {
      const res = await apiClient.get('/chat/users');
      setChatUsers(res.data || []);
    } catch (e) { logger.error(e); }
  };

  // Delete group state — guarded by a confirmation dialog so admins
  // don't accidentally wipe a whole conversation.
  const [deleteTarget, setDeleteTarget] = useState(null);  // the convo about to be deleted
  const [deletingGroup, setDeletingGroup] = useState(false);

  // Who can delete THIS group? Group creator OR super_admin / admin.
  // DMs are never deletable. Broadcasts are never deletable.
  const canDeleteActive =
    !!activeConvo &&
    activeConvo.is_group &&
    !activeConvo.is_broadcast &&
    (activeConvo.created_by === user?.id || ['super_admin', 'admin'].includes(user?.role));

  const handleDeleteGroup = async () => {
    if (!deleteTarget) return;
    setDeletingGroup(true);
    try {
      await apiClient.delete(`/chat/conversations/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.group_name || deleteTarget.display_name || 'Group'}" deleted`);
      // Clear local state: if we were inside the deleted convo, drop the
      // message list + active selection so the empty-state shows.
      if (activeConvo?.id === deleteTarget.id) {
        setActiveConvo(null);
        setMessages([]);
        setShowMobile(false);
      }
      setDeleteTarget(null);
      await fetchConversations();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not delete group';
      toast.error(detail);
      logger.error('Delete group failed:', e);
    } finally {
      setDeletingGroup(false);
    }
  };

  const toggleMember = (u) => {
    setSelectedMembers(prev =>
      prev.find(m => m.id === u.id)
        ? prev.filter(m => m.id !== u.id)
        : [...prev, u]
    );
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return;
    try {
      const res = await apiClient.post('/chat/conversations', {
        participant_ids: selectedMembers.map(m => m.id),
        is_group: true,
        group_name: groupName.trim(),
      });
      setShowCreateGroup(false);
      await fetchConversations();
      openConversation(res.data);
    } catch (e) { logger.error(e); }
  };

  const openNewChatDialog = async () => {
    setShowNewChat(true);
    try {
      const res = await apiClient.get('/chat/users');
      setChatUsers(res.data || []);
    } catch (e) { logger.error(e); }
  };

  const filteredUsers = chatUsers.filter(u =>
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.department?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="chat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-blue-500" /> Team Chat
          </h2>
          <p className="text-sm text-muted-foreground">Internal messaging across departments</p>
        </div>
        {totalUnread > 0 && <Badge className="bg-red-500 text-white">{totalUnread} unread</Badge>}
      </div>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Conversation List */}
        <Card className={`w-full md:w-80 flex-shrink-0 flex flex-col ${showMobile && activeConvo ? 'hidden md:flex' : 'flex'}`}>
          <CardHeader className="pb-2 px-3 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Conversations</CardTitle>
              <div className="flex items-center gap-1">
                {canBroadcast && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-amber-500 hover:text-amber-600 hover:bg-amber-500/10"
                    onClick={() => setBroadcastOpen(true)}
                    title="Broadcast to All"
                    data-testid="broadcast-btn"
                  >
                    <Megaphone className="h-4 w-4" />
                  </Button>
                )}
                {canCreateGroup && (
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={openCreateGroupDialog} title="Create Group" data-testid="new-group-btn">
                    <Users className="h-4 w-4" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={openNewChatDialog} data-testid="new-chat-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-2 pb-2 flex-1 overflow-auto">
            {loading ? (
              <p className="text-center text-muted-foreground text-sm py-8">Loading...</p>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No conversations yet</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={openNewChatDialog}>Start a Chat</Button>
              </div>
            ) : (
              <div className="space-y-1">
                {conversations.map(c => (
                  <button
                    key={c.id}
                    className={`w-full text-left p-2.5 rounded-lg transition-colors ${activeConvo?.id === c.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/50'}`}
                    onClick={() => openConversation(c)}
                    data-testid={`convo-${c.id}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="relative w-9 h-9 flex-shrink-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium ${c.is_broadcast ? 'bg-amber-500' : 'bg-blue-600'}`}>
                          {c.is_broadcast ? <Megaphone className="h-4 w-4" /> : c.is_group ? <Users className="h-4 w-4" /> : (c.display_name?.charAt(0) || '?')}
                        </div>
                        {!c.is_group && c.other_participants?.[0]?.status && (
                          <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background ${STATUS_DOT[c.other_participants[0].status] || STATUS_DOT.offline}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">{c.display_name}</p>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-1">{timeAgo(c.last_message_at)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {c.last_message_sender ? `${c.last_message_sender.split(' ')[0]}: ` : ''}{c.last_message_preview || 'No messages yet'}
                        </p>
                      </div>
                      {c.unread_count > 0 && (
                        <Badge className="bg-blue-500 text-white text-[10px] px-1.5 h-5 flex-shrink-0">{c.unread_count}</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Area */}
        <Card className={`flex-1 flex flex-col min-h-0 ${!showMobile && !activeConvo ? 'hidden md:flex' : 'flex'} ${showMobile && activeConvo ? 'flex' : !activeConvo ? '' : ''}`}>
          {activeConvo ? (
            <>
              <CardHeader className="pb-2 px-4 pt-3 border-b flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={() => { setShowMobile(false); setActiveConvo(null); }}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                    {activeConvo.is_group ? <Users className="h-4 w-4" /> : (activeConvo.display_name?.charAt(0) || '?')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{activeConvo.display_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {activeConvo.other_participants?.map(p => formatRole(p.role)).join(', ')}
                    </p>
                  </div>
                  {/* Group actions menu — currently only Delete Group. Only
                      rendered when the current user is allowed to delete
                      (creator or admin) and the convo is an actual group. */}
                  {canDeleteActive && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="group-menu-btn" title="Group options">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-700 focus:bg-red-500/10 cursor-pointer"
                          onSelect={() => setDeleteTarget(activeConvo)}
                          data-testid="delete-group-menu-item"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 overflow-hidden flex flex-col">
                <ScrollArea className="flex-1 px-4 py-3">
                  {msgLoading ? (
                    <p className="text-center text-muted-foreground text-sm py-8">Loading messages...</p>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/20 mb-2" />
                      <p className="text-sm text-muted-foreground">Start the conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map((msg, idx) => {
                        const isOwn = msg.sender_id === user?.id;
                        const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                        if (msg.is_broadcast) {
                          // Announcements render as a full-width amber banner — no
                          // left/right alignment, no avatar bubble. Stands out
                          // visually from regular DMs and group chatter.
                          return (
                            <div key={msg.id} className="flex justify-center my-2" data-testid={`msg-broadcast-${msg.id}`}>
                              <div className="w-full max-w-2xl rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                                <div className="flex items-center gap-2 mb-1 text-amber-700 dark:text-amber-300 text-xs font-semibold uppercase tracking-wider">
                                  <Megaphone className="h-3.5 w-3.5" />
                                  Announcement · {msg.sender_name}
                                </div>
                                <div className="text-sm whitespace-pre-wrap text-foreground">{msg.text}</div>
                                <p className="text-[10px] text-muted-foreground mt-1.5">
                                  {new Date(msg.created_at).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] ${isOwn ? 'items-end' : 'items-start'}`}>
                              {showAvatar && !isOwn && (
                                <p className="text-[11px] text-muted-foreground mb-0.5 ml-1">{msg.sender_name}</p>
                              )}
                              <div className={`px-3 py-2 rounded-2xl text-sm ${isOwn ? 'bg-blue-600 text-white rounded-br-md' : 'bg-muted rounded-bl-md'}`} data-testid={`msg-${msg.id}`}>
                                {msg.text}
                              </div>
                              <p className={`text-[10px] text-muted-foreground mt-0.5 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
                                {new Date(msg.created_at).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>
                {activeConvo?.is_broadcast && !canBroadcast ? (
                  // Read-only banner for non-admin viewers of the
                  // Announcements channel. The Reply box is suppressed
                  // entirely so it's clear this is a one-way broadcast.
                  <div className="border-t p-3 flex-shrink-0 bg-amber-500/5">
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Megaphone className="h-3.5 w-3.5 text-amber-500" />
                      Read-only channel · only admins can post announcements.
                    </div>
                  </div>
                ) : (
                  <div className="border-t p-3 flex gap-2 flex-shrink-0">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={activeConvo?.is_broadcast ? 'Send another announcement…' : 'Type a message...'}
                      className="flex-1"
                      data-testid="chat-message-input"
                    />
                    <Button size="icon" onClick={sendMessage} disabled={!newMessage.trim() || sending} data-testid="send-message-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
                <p className="text-muted-foreground">Select a conversation or start a new one</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={openNewChatDialog}>
                  <Plus className="h-4 w-4 mr-1" /> New Chat
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Chat Dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Start New Conversation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or department..."
                className="pl-9"
                data-testid="chat-user-search"
              />
            </div>
            <ScrollArea className="max-h-[350px]">
              <div className="space-y-1">
                {filteredUsers.map(u => (
                  <button
                    key={u.id}
                    className="w-full text-left p-2.5 rounded-lg hover:bg-muted/50 transition-colors flex items-center gap-3"
                    onClick={() => startNewChat(u)}
                    data-testid={`chat-start-${u.id}`}
                  >
                    <div className="relative w-9 h-9 flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-slate-600 flex items-center justify-center text-white text-sm font-medium">
                        {u.full_name?.charAt(0) || '?'}
                      </div>
                      <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-background ${STATUS_DOT[u.status] || STATUS_DOT.offline}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm flex items-center gap-1.5">
                        {u.full_name}
                        {!u.is_active && <Badge variant="secondary" className="text-[9px] px-1 py-0">Inactive</Badge>}
                      </p>
                      <p className={`text-xs ${ROLE_COLORS[u.role] || 'text-muted-foreground'}`}>
                        {formatRole(u.role)} {u.department ? `- ${u.department}` : ''}
                      </p>
                    </div>
                    <span className={`text-[9px] ${u.status === 'online' ? 'text-emerald-500' : u.status === 'away' ? 'text-amber-500' : 'text-muted-foreground'}`}>
                      {u.status === 'online' ? 'Online' : u.status === 'away' ? 'Away' : 'Offline'}
                    </span>
                  </button>
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-center text-muted-foreground text-sm py-4">No users found</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Group Chat</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={groupName} onChange={e => setGroupName(e.target.value)}
              placeholder="Group name (e.g., CS Team, Sales Leads)"
              data-testid="group-name-input" />
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedMembers.map(m => (
                  <Badge key={m.id} variant="secondary" className="text-xs cursor-pointer hover:bg-destructive/20"
                    onClick={() => toggleMember(m)}>
                    {m.full_name?.split(' ')[0]} &times;
                  </Badge>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                placeholder="Search members to add..." className="pl-9" data-testid="group-member-search" />
            </div>
            <ScrollArea className="max-h-[250px]">
              <div className="space-y-1">
                {filteredUsers.map(u => {
                  const isSelected = selectedMembers.some(m => m.id === u.id);
                  return (
                    <button key={u.id} onClick={() => toggleMember(u)}
                      className={`w-full text-left p-2 rounded-lg transition-colors flex items-center gap-3 ${isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}`}
                      data-testid={`group-member-${u.id}`}>
                      <div className="w-7 h-7 rounded-full bg-slate-600 flex items-center justify-center text-white text-xs font-medium">
                        {u.full_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{u.full_name}</p>
                        <p className="text-[10px] text-muted-foreground">{formatRole(u.role)}</p>
                      </div>
                      {isSelected && <span className="text-primary text-xs font-medium">Added</span>}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
            <Button onClick={createGroup} disabled={!groupName.trim() || selectedMembers.length === 0}
              className="w-full" data-testid="create-group-btn">
              Create Group ({selectedMembers.length} members)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={(o) => { if (!broadcastSending) setBroadcastOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-500" />
              Broadcast to Everyone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
              This message will land in <strong>every active employee's</strong> Team Chat as an announcement,
              and trigger a desktop notification for anyone who has them enabled.
            </div>
            <Textarea
              placeholder="Write your announcement…"
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              rows={6}
              maxLength={4000}
              autoFocus
              disabled={broadcastSending}
              data-testid="broadcast-text"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground tabular-nums">
                {broadcastText.length} / 4000
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setBroadcastOpen(false)}
                  disabled={broadcastSending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendBroadcast}
                  disabled={broadcastSending || !broadcastText.trim()}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="broadcast-send"
                >
                  {broadcastSending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Megaphone className="h-4 w-4 mr-2" /> Send announcement</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Group confirmation — guarded by AlertDialog so a misclick
          on the kebab menu can't wipe a real conversation. */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open && !deletingGroup) setDeleteTarget(null); }}
      >
        <AlertDialogContent data-testid="delete-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete this group?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-semibold text-foreground">
                    {deleteTarget?.group_name || deleteTarget?.display_name || 'This group'}
                  </span>{' '}
                  will be permanently deleted for everyone. This cannot be undone.
                </p>
                <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
                  <li>All members will be removed.</li>
                  <li>The chat history (every message ever sent here) will be cleared.</li>
                  <li>Notifications pointing to this group will disappear from members&apos; bells.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingGroup} data-testid="delete-group-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              disabled={deletingGroup}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-group-confirm"
            >
              {deletingGroup ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Delete group</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
